import { ToastProps } from '@chakra-ui/react';

import { EVSE_FACTORY_PIN_CODE } from '../constants';
import { DetectedEvseModel, resolveEvseVariant } from '../evse-variant';
import { fetchDeviceProductInfo, getAuthToken } from '../wifi-configuration/wifi-connect-helper';

// Caps the extra Soft-AP round trips so variant detection can never stall onboarding.
export const EVSE_VARIANT_DETECTION_TIMEOUT_MS = 15000;

/**
 * Reads the model number (`CommercialReference`) from the local EVSE API while the phone is on the
 * charger's Soft-AP and resolves the EVSE variant from it. Rejects on login/fetch failure or timeout.
 */
export async function detectEvseModelFromSoftAp(
  toast: (props: ToastProps) => void,
  timeoutMs = EVSE_VARIANT_DETECTION_TIMEOUT_MS
): Promise<DetectedEvseModel> {
  const controller = new AbortController();
  const detection = (async (): Promise<DetectedEvseModel> => {
    const token = await getAuthToken(EVSE_FACTORY_PIN_CODE, toast, controller.signal);
    const productInfo = await fetchDeviceProductInfo(token, controller.signal);
    const commercialReference = productInfo?.CommercialReference;
    const modelNumber =
      typeof commercialReference === 'string' && commercialReference.trim() ? commercialReference.trim() : null;
    return { modelNumber, evseVariant: resolveEvseVariant(modelNumber) };
  })();

  let timer: ReturnType<typeof setTimeout> | undefined;
  const timedOut = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      // Stops login retries and the product-info request from starting after setup has moved on.
      controller.abort();
      reject(new Error('EVSE variant detection timed out'));
    }, timeoutMs);
  });

  try {
    return await Promise.race([detection, timedOut]);
  } finally {
    clearTimeout(timer);
  }
}

export function detectVirtualEvseModelFromSsid(ssid: string): DetectedEvseModel {
  const virtualSsidMatch = ssid.match(/^virtual-(.+)_(.+)$/);
  const commercialReference = virtualSsidMatch?.[1] || null;
  const modelNumber =
    typeof commercialReference === 'string' && commercialReference.trim() ? commercialReference.trim() : null;
  return { modelNumber, evseVariant: resolveEvseVariant(modelNumber) };
}
